import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useSettings from "@hooks/useSettings";
import { filterLocalesBySpaceVersion } from "@modules/localization";

/**
 * LanguageMenu에 넘길 언어 목록·현재 언어·라벨·변경 핸들러를 만드는 공용 훅.
 * 프로필 메뉴와 welcome 페이지 Footer가 같은 언어 게이트 규칙을 쓰도록 한곳에 둔다. (DAM-973)
 *
 * @returns {{ locales: Array, locale: string, label: string | undefined, onChange: Function }}
 *   LanguageMenu의 locales / locale / label / onClick에 그대로 연결한다.
 */
const useLanguageMenu = () => {
    const { i18n } = useTranslation();
    const { i18nLng, updateLocaleLanguage } = useSettings({ type: "General" });
    const spaceVersion = useSelector(({ stage }) => stage.version);

    const [cachedLocales] = useState(() => JSON.parse(localStorage.getItem("i18nResources")));
    // 라벨은 게이트와 무관하게 전체 목록에서 찾는다 (현재 적용된 언어명이 그대로 보이도록)
    const label = useMemo(
        () => cachedLocales?.find((element) => element.value === i18nLng)?.label,
        [cachedLocales, i18nLng],
    );

    // Space 버전에 따라 노출 언어를 게이팅한다. Space 전환 시 즉시 반영되도록 렌더 시점에 필터링한다. (DAM-463)
    // 로그인 전에는 Space 버전이 없어 버전 게이트가 걸린 언어는 노출되지 않는다.
    const locales = useMemo(
        () => filterLocalesBySpaceVersion(cachedLocales, spaceVersion),
        [cachedLocales, spaceVersion],
    );

    const onChange = (lng) => {
        i18n.changeLanguage(lng).then(() => {
            updateLocaleLanguage(lng);
            window.location.reload();
        });
    };

    return { locales, locale: i18nLng, label, onChange };
};

export default useLanguageMenu;
